import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import {
  User, Star, Lock, Coins, Check, Image as ImageIcon, Flag,
  Package, Link as LinkIcon, PlayCircle, Clock, PaperPlaneTilt,
} from '@phosphor-icons/react';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import ReportModal from '../components/ReportModal';

const API = process.env.REACT_APP_BACKEND_URL;

const CreatorProfilePage = () => {
  const { userId } = useParams();
  const { user, refreshUser } = useAuth();
  const [profile, setProfile] = useState(null);
  const [content, setContent] = useState([]);
  const [bundles, setBundles] = useState([]);
  const [externalSubs, setExternalSubs] = useState([]);
  const [tab, setTab] = useState('about');
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);
  const [showReport, setShowReport] = useState(false);

  const fetchProfile = useCallback(async () => {
    try {
      const [profileRes, contentRes, bundlesRes, extRes] = await Promise.all([
        axios.get(`${API}/api/users/profile/${userId}`, { withCredentials: true }),
        axios.get(`${API}/api/content/creator/${userId}`, { withCredentials: true }),
        axios.get(`${API}/api/store/bundles/creator/${userId}`, { withCredentials: true }),
        axios.get(`${API}/api/store/external-subs/creator/${userId}`, { withCredentials: true }),
      ]);
      setProfile(profileRes.data);
      setContent(contentRes.data);
      setBundles(bundlesRes.data);
      setExternalSubs(extRes.data);
    } catch (err) {
      toast.error('Failed to load profile');
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => { fetchProfile(); }, [fetchProfile]);

  const subscribeTokens = async (tierId) => {
    setBusy(`tier-${tierId}`);
    try {
      const res = await axios.post(`${API}/api/store/fan-sub/subscribe`,
        { tier_id: tierId }, { withCredentials: true });
      toast.success(res.data.message);
      await refreshUser();
      fetchProfile();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Subscribe failed');
    } finally { setBusy(null); }
  };

  const purchaseBundle = async (bundleId) => {
    setBusy(`bundle-${bundleId}`);
    try {
      const res = await axios.post(`${API}/api/store/bundles/${bundleId}/purchase`,
        {}, { withCredentials: true });
      toast.success(res.data.message);
      await refreshUser();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Purchase failed');
    } finally { setBusy(null); }
  };

  const purchaseExternal = async (subId) => {
    setBusy(`ext-${subId}`);
    try {
      const res = await axios.post(`${API}/api/store/external-subs/${subId}/purchase`,
        {}, { withCredentials: true });
      if (res.data.delivered) {
        toast.success('Access link delivered! Check "My Orders".');
      } else {
        toast.success('Order placed. Creator will send you the personalized link shortly.');
      }
      await refreshUser();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Purchase failed');
    } finally { setBusy(null); }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <User className="w-20 h-20 text-slate-600 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-white">Profile not found</h2>
        </div>
      </div>
    );
  }

  const isOwnProfile = user?.id === userId;
  const photos = content.filter(c => c.content_type === 'image');
  const videos = content.filter(c => c.content_type === 'video');

  const TABS = [
    { key: 'about', label: 'About', icon: User },
    { key: 'photos', label: `Photos (${photos.length})`, icon: ImageIcon },
    { key: 'videos', label: `Videos (${videos.length})`, icon: PlayCircle },
    { key: 'bundles', label: `Bundles (${bundles.length})`, icon: Package },
    { key: 'fansubs', label: 'Fan Access', icon: Star },
    { key: 'external', label: `Links (${externalSubs.length})`, icon: LinkIcon },
  ];

  return (
    <div className="max-w-5xl mx-auto px-4 py-6" data-testid="creator-profile-page">
      {/* Cover + Avatar */}
      <div className="relative mb-20">
        <div
          className="h-48 md:h-64 rounded-3xl bg-gradient-to-br from-blue-600 to-indigo-600 overflow-hidden"
          style={profile.cover_url ? { backgroundImage: `url(${profile.cover_url})`, backgroundSize: 'cover', backgroundPosition: 'center' } : {}}
        >
          <div className="absolute inset-0 bg-black/30" />
        </div>
        <div className="absolute -bottom-16 left-6 flex items-end gap-4">
          <div className="relative">
            <div className="w-32 h-32 rounded-full border-4 border-[#09090b] bg-slate-800 overflow-hidden">
              {profile.avatar_url ? (
                <img src={profile.avatar_url} alt={profile.display_name} className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full flex items-center justify-center">
                  <User className="w-12 h-12 text-slate-500" />
                </div>
              )}
            </div>
            {profile.role === 'creator' && (
              <span className="absolute -top-2 -right-2 px-2 py-1 bg-amber-500 text-amber-950 text-xs font-bold rounded-full flex items-center gap-1">
                <Star weight="fill" className="w-3 h-3" /> Creator
              </span>
            )}
          </div>
          <div className="pb-2">
            <h1 className="font-heading text-2xl font-bold text-white">{profile.display_name}</h1>
            {profile.gender && <p className="text-slate-400">{profile.gender}</p>}
          </div>
        </div>

        {!isOwnProfile && (
          <button
            onClick={() => setShowReport(true)}
            className="absolute top-4 right-4 p-2 text-slate-500 hover:text-amber-400 hover:bg-amber-500/10 rounded-full transition-colors"
            data-testid="report-user-btn"
          >
            <Flag className="w-5 h-5" />
          </button>
        )}
      </div>

      {/* Quick stats */}
      <div className="grid grid-cols-3 gap-4 mb-6">
        <div className="glass-light rounded-xl p-4 text-center">
          <div className="text-2xl font-bold text-white">{profile.content_count}</div>
          <div className="text-sm text-slate-400">Posts</div>
        </div>
        <div className="glass-light rounded-xl p-4 text-center">
          <div className="text-2xl font-bold text-blue-500">{profile.subscriber_count}</div>
          <div className="text-sm text-slate-400">Subscribers</div>
        </div>
        <div className="glass-light rounded-xl p-4 text-center">
          {profile.is_subscribed ? (
            <>
              <Check weight="bold" className="w-8 h-8 text-green-500 mx-auto" />
              <div className="text-sm text-green-400">Subscribed</div>
            </>
          ) : (
            <>
              <Lock className="w-8 h-8 text-slate-500 mx-auto" />
              <div className="text-sm text-slate-400">Not Subscribed</div>
            </>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 mb-6 overflow-x-auto no-scrollbar" data-testid="profile-tabs">
        {TABS.map(({ key, label, icon: Icon }) => (
          <button
            key={key}
            onClick={() => setTab(key)}
            className={`flex-shrink-0 px-4 py-2 rounded-full font-medium transition-colors flex items-center gap-2 text-sm ${
              tab === key ? 'bg-blue-500 text-white' : 'bg-slate-800 text-slate-400 hover:text-white'
            }`}
            data-testid={`profile-tab-${key}`}
          >
            <Icon className="w-4 h-4" /> {label}
          </button>
        ))}
      </div>

      {/* ABOUT */}
      {tab === 'about' && (
        <div className="space-y-6" data-testid="tab-about">
          {profile.bio && (
            <div>
              <h2 className="text-sm font-bold uppercase tracking-wider text-slate-500 mb-2">Bio</h2>
              <p className="text-slate-300">{profile.bio}</p>
            </div>
          )}
          {profile.interests?.length > 0 && (
            <div>
              <h2 className="text-sm font-bold uppercase tracking-wider text-slate-500 mb-3">Interests</h2>
              <div className="flex flex-wrap gap-2">
                {profile.interests.map(i => (
                  <span key={i} className="px-3 py-1.5 bg-slate-800 text-slate-300 rounded-full text-sm">{i}</span>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* PHOTOS / VIDEOS */}
      {(tab === 'photos' || tab === 'videos') && (
        <ContentGrid items={tab === 'photos' ? photos : videos} emptyLabel={tab === 'photos' ? 'No photos yet' : 'No videos yet'} />
      )}

      {/* BUNDLES */}
      {tab === 'bundles' && (
        <div data-testid="tab-bundles">
          {bundles.length === 0 ? (
            <EmptyState icon={Package} label="No bundles yet" />
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {bundles.map(b => (
                <motion.div key={b.id} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
                  className="glass-light rounded-2xl overflow-hidden" data-testid={`profile-bundle-${b.id}`}>
                  <div className="aspect-video bg-gradient-to-br from-blue-900/30 to-indigo-900/30 relative">
                    {b.preview_url ? (
                      <img src={b.preview_url} alt="" className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <Package className="w-12 h-12 text-white/30" />
                      </div>
                    )}
                    <span className="absolute top-2 right-2 px-2 py-1 bg-black/70 text-white text-xs font-bold rounded-full">
                      {b.item_count} items
                    </span>
                  </div>
                  <div className="p-4">
                    <h4 className="font-bold text-white truncate">{b.title}</h4>
                    {b.description && <p className="text-xs text-slate-500 mt-1 line-clamp-2">{b.description}</p>}
                    <div className="mt-3 flex items-center justify-between">
                      <span className="flex items-center gap-1 text-amber-400 font-bold">
                        <Coins weight="fill" className="w-4 h-4" /> {b.price_tokens}
                      </span>
                      {!isOwnProfile && (
                        <button onClick={() => purchaseBundle(b.id)} disabled={busy === `bundle-${b.id}`}
                          className="px-4 py-1.5 bg-blue-500 text-white rounded-full text-sm font-semibold hover:bg-blue-600 disabled:opacity-50"
                          data-testid={`buy-bundle-${b.id}`}>
                          {busy === `bundle-${b.id}` ? 'Processing…' : 'Unlock'}
                        </button>
                      )}
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* FAN SUBS */}
      {tab === 'fansubs' && (
        <div data-testid="tab-fansubs">
          {(profile.subscription_tiers?.length || 0) === 0 ? (
            <EmptyState icon={Star} label="No fan tiers yet" />
          ) : (
            <div className="grid md:grid-cols-2 gap-4">
              {profile.subscription_tiers.map((t, idx) => {
                const priceTokens = Math.round((t.price_monthly || 0) * 20);
                return (
                  <motion.div key={t.id} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: idx * 0.1 }}
                    className={`relative rounded-2xl p-6 border ${idx === 0 ? 'bg-blue-500/10 border-blue-500/30' : 'glass-light border-white/5'}`}
                    data-testid={`profile-tier-${t.id}`}>
                    {idx === 0 && (
                      <span className="absolute -top-3 left-4 px-3 py-1 bg-blue-500 text-white text-xs font-bold rounded-full">Popular</span>
                    )}
                    <h3 className="font-heading text-xl font-bold text-white mb-2">{t.name}</h3>
                    <div className="flex items-baseline gap-1 mb-4">
                      <Coins weight="fill" className="w-5 h-5 text-amber-400" />
                      <span className="text-3xl font-bold text-white">{priceTokens}</span>
                      <span className="text-slate-500">tokens/month</span>
                    </div>
                    {t.benefits?.length > 0 && (
                      <ul className="space-y-2 mb-6">
                        {t.benefits.map((b, i) => (
                          <li key={`${t._id || t.id || 'tier'}-${i}-${b}`} className="flex items-center gap-2 text-sm text-slate-300">
                            <Check className="w-4 h-4 text-green-500" /> {b}
                          </li>
                        ))}
                      </ul>
                    )}
                    {!isOwnProfile && !profile.is_subscribed && (
                      <button onClick={() => subscribeTokens(t.id)} disabled={busy === `tier-${t.id}`}
                        className={`w-full py-3 rounded-full font-semibold disabled:opacity-50 ${
                          idx === 0 ? 'bg-blue-500 text-white hover:bg-blue-600' : 'bg-white/10 text-white hover:bg-white/20'
                        }`}
                        data-testid={`profile-subscribe-${t.id}`}>
                        {busy === `tier-${t.id}` ? 'Processing…' : 'Subscribe'}
                      </button>
                    )}
                  </motion.div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* EXTERNAL SUBS */}
      {tab === 'external' && (
        <div data-testid="tab-external">
          {externalSubs.length === 0 ? (
            <EmptyState icon={LinkIcon} label="No paid external access yet" />
          ) : (
            <div className="grid md:grid-cols-2 gap-4">
              {externalSubs.map(e => (
                <div key={e.id} className="glass-light rounded-2xl p-5" data-testid={`profile-ext-${e.id}`}>
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-xs px-2 py-0.5 bg-blue-500/20 text-blue-400 rounded-full font-medium">{e.platform}</span>
                    {e.has_auto_link && (
                      <span className="flex items-center gap-1 text-green-400 text-xs">
                        <PaperPlaneTilt className="w-3 h-3" /> Instant delivery
                      </span>
                    )}
                  </div>
                  <h4 className="font-semibold text-white">{e.title}</h4>
                  {e.description && <p className="text-sm text-slate-500 mt-1">{e.description}</p>}
                  <div className="flex items-center justify-between mt-4 pt-3 border-t border-white/5">
                    <div className="flex items-center gap-3 text-sm">
                      <span className="flex items-center gap-1 text-amber-400 font-bold">
                        <Coins weight="fill" className="w-4 h-4" /> {e.price_tokens}
                      </span>
                      <span className="flex items-center gap-1 text-slate-500">
                        <Clock className="w-4 h-4" /> {e.duration_days}d
                      </span>
                    </div>
                    {!isOwnProfile && (
                      <button onClick={() => purchaseExternal(e.id)} disabled={busy === `ext-${e.id}`}
                        className="px-4 py-1.5 bg-blue-500 text-white rounded-full text-sm font-semibold hover:bg-blue-600 disabled:opacity-50"
                        data-testid={`buy-ext-${e.id}`}>
                        {busy === `ext-${e.id}` ? 'Processing…' : 'Buy Access'}
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      <ReportModal open={showReport} onOpenChange={setShowReport}
        targetType="user" targetId={userId} targetName={profile?.display_name || ''} />
    </div>
  );
};

const EmptyState = ({ icon: Icon, label }) => (
  <div className="text-center py-12 glass-light rounded-2xl">
    <Icon className="w-12 h-12 text-slate-600 mx-auto mb-3" />
    <p className="text-slate-500">{label}</p>
  </div>
);

const ContentGrid = ({ items, emptyLabel }) => {
  if (items.length === 0) return <EmptyState icon={ImageIcon} label={emptyLabel} />;
  return (
    <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
      {items.map(item => (
        <div key={item.id}
          className="aspect-square rounded-xl overflow-hidden bg-slate-900 relative group"
          data-testid={`content-item-${item.id}`}>
          {item.is_locked ? (
            <>
              <img src={item.thumbnail_url || item.media_url} alt={item.title}
                className="w-full h-full object-cover content-locked" />
              <div className="absolute inset-0 flex items-center justify-center bg-black/30 backdrop-blur-sm">
                <div className="text-center">
                  <Lock className="w-8 h-8 text-white mx-auto mb-2" />
                  <span className="text-white text-sm font-medium">{item.price_tokens} tokens</span>
                </div>
              </div>
            </>
          ) : (
            <img src={item.media_url} alt={item.title} className="w-full h-full object-cover" />
          )}
        </div>
      ))}
    </div>
  );
};

export default CreatorProfilePage;
